import React from 'react';
import { Archive, ArchiveRestore } from 'lucide-react';
import type { AppNotification } from '../../types/notifications';
import { NotificationItemContent } from './NotificationItemContent';

type Props = {
  notification: AppNotification;
  onOpen: (n: AppNotification) => void;
  /** `archive` im Posteingang, `restore` im Archiv. */
  action: 'archive' | 'restore';
  onAction: (n: AppNotification) => void;
  /** Per Tastatur markiert (Seite „Alle Benachrichtigungen“). */
  active?: boolean;
  onHover?: () => void;
  className?: string;
};

/**
 * Eine Benachrichtigung. Beim Darüberfahren (oder markiert) ersetzt das Archiv- bzw. Wiederherstellen-Symbol
 * das Datum oben rechts, wie in Gmail/Linear. Am Handy steht das Symbol unten rechts, das Datum bleibt.
 */
export function NotificationRow({ notification: n, onOpen, action, onAction, active, onHover, className }: Props) {
  const label = action === 'archive' ? 'Archivieren' : 'Wiederherstellen';
  const Icon = action === 'archive' ? Archive : ArchiveRestore;
  return (
    <li className="group relative" onMouseEnter={onHover}>
      {active ? <span className="absolute inset-y-0 left-0 z-[1] w-0.5 bg-pipedrive-blue" aria-hidden /> : null}
      <button
        type="button"
        onClick={() => onOpen(n)}
        className={`w-full text-left border-b border-app-border transition-colors hover:bg-app-hover/70 ${
          active ? 'bg-app-hover/70' : !n.is_read ? 'bg-blue-500/8' : ''
        } ${className ?? 'px-4 py-3'}`}
      >
        <NotificationItemContent
          notification={n}
          showUnreadDot
          timeClassName={`md:group-hover:invisible md:group-focus-within:invisible ${active ? 'md:invisible' : ''}`}
        />
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onAction(n);
        }}
        className={`absolute right-2 bottom-2 inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-app-text-subtle transition-colors max-md:bg-app-surface hover:bg-app-surface hover:text-app-text focus-visible:opacity-100 md:bottom-auto md:top-2 ${
          active ? 'md:opacity-100' : 'md:opacity-0 md:group-hover:opacity-100'
        }`}
        title={action === 'archive' ? 'Archivieren (E)' : label}
        aria-label={label}
      >
        <Icon className="h-4 w-4" />
        <span className="md:hidden">{label}</span>
      </button>
    </li>
  );
}
